<template>
  <v-container class="reschedule-settings-page pa-6" :class="{ 'editing-mode': isOptionsEditing }" style="max-width: 1400px">
    <!-- Card 1: 改約限制設定 -->
    <v-card class="settings-card mb-6" elevation="1" rounded="lg">
      <v-card-text class="pa-6">
        <reschedule-setting ref="settingRef" />
      </v-card-text>
    </v-card>

    <!-- Card 2: 改約選項設定（含預覽） -->
    <v-card class="settings-card" elevation="1" rounded="lg">
      <v-card-text class="pa-6">
        <!-- 標題與編輯按鈕 -->
        <div class="section-header d-flex justify-space-between align-center mb-4">
          <div class="section-title">
            <div class="bullet">改約選項設定</div>
          </div>
          <div class="edit-controls">
            <v-btn
              v-if="!isOptionsEditing"
              outlined
              color="primary"
              class="rounded-lg"
              @click="startOptionsEditing"
            >
              <v-icon left small>mdi-pencil</v-icon>
              {{ $t('GENERAL.edit') }}
            </v-btn>
          </div>
        </div>

        <!-- 左右分欄版面（md 以上才分欄，螢幕越小預覽佔比越小） -->
        <v-row>
          <!-- 左側：改約選項編輯器 -->
          <v-col cols="12" md="6" lg="5" class="options-editor-section">
            <reschedule-options-editor
              ref="optionsEditorRef"
              :hide-edit-controls="true"
              @editing-change="onEditingChange"
              @options-change="onOptionsChange"
              @save-success="onSaveSuccess"
            />
          </v-col>

          <!-- 右側：預覽 -->
          <v-col cols="12" md="6" lg="7" class="preview-section">
            <reschedule-preview
              :reasons="previewReasons"
              :contacts="previewContacts"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- 編輯模式下的底部固定按鈕 -->
    <div v-if="isOptionsEditing" class="fixed-bottom-bar">
      <v-btn
        text
        class="mr-2 rounded-lg"
        @click="cancelOptionsEditing"
      >
        {{ $t('GENERAL.cancel') }}
      </v-btn>
      <v-btn
        color="primary"
        class="rounded-lg"
        @click="saveOptionsEditing"
      >
        {{ $t('GENERAL.save') }}
      </v-btn>
    </div>
  </v-container>
</template>

<script>
import RescheduleSetting from './RescheduleSetting.vue'
import RescheduleOptionsEditor from './RescheduleOptionsEditor.vue'
import ReschedulePreview from './ReschedulePreview.vue'

export default {
  name: 'RescheduleSettingsPage',

  components: {
    RescheduleSetting,
    RescheduleOptionsEditor,
    ReschedulePreview
  },

  data() {
    return {
      isOptionsEditing: false,
      // 用於傳遞給預覽的資料
      previewReasons: null,
      previewContacts: null
    }
  },

  mounted() {
    // 初始化預覽資料
    this.$nextTick(() => {
      this.updatePreviewData()
    })

    // 監聽瀏覽器重新整理或關閉
    window.addEventListener('beforeunload', this.handleBeforeUnload)
  },

  beforeDestroy() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
  },

  methods: {
    updatePreviewData() {
      if (this.$refs.optionsEditorRef) {
        const options = this.$refs.optionsEditorRef.currentOptions
        // 只有在有資料時才更新預覽（避免傳遞空陣列導致 Preview 不再自己 fetch）
        if (options && options.reasons && options.reasons.length > 0) {
          this.previewReasons = options.reasons
          this.previewContacts = options.contacts
        }
      }
    },

    startOptionsEditing() {
      if (this.$refs.optionsEditorRef) {
        this.$refs.optionsEditorRef.startEditing()
        this.isOptionsEditing = true
      }
    },

    cancelOptionsEditing() {
      if (this.$refs.optionsEditorRef) {
        this.$refs.optionsEditorRef.cancelEditing()
        this.isOptionsEditing = false
        this.updatePreviewData()
      }
    },

    async saveOptionsEditing() {
      if (this.$refs.optionsEditorRef) {
        const success = await this.$refs.optionsEditorRef.saveOptions()
        if (success) {
          this.isOptionsEditing = false
          this.updatePreviewData()
        }
      }
    },

    onEditingChange(isEditing) {
      this.isOptionsEditing = isEditing
      if (!isEditing) {
        this.updatePreviewData()
      }
    },

    onOptionsChange(options) {
      // 即時更新預覽資料（由子元件透過事件發射）
      if (options && options.reasons && options.reasons.length > 0) {
        this.previewReasons = options.reasons
        this.previewContacts = options.contacts
      }
    },

    onSaveSuccess() {
      this.updatePreviewData()
    },

    handleBeforeUnload(e) {
      const isSettingEditing = this.$refs.settingRef?.isEditing || false
      if (this.isOptionsEditing || isSettingEditing) {
        e.preventDefault()
        e.returnValue = ''
        return ''
      }
    }
  }
}
</script>

<style scoped lang="scss">
// v-container handles padding (pa-6) and max-width (inline style)

.settings-card {
  overflow: visible;

  ::v-deep .v-card__text {
    overflow: visible;
  }
}

.section-title {
  .bullet::before {
    content: "";
    background: var(--v-tiffany-10-base);
    padding-inline: 5px;
    height: 10px;
    margin-inline-end: 10px;
    display: inline-block;
  }
}

.options-editor-section {
  min-width: 0; // 防止溢出

  // 移除 RescheduleOptionsEditor 的內部 v-card 邊距
  ::v-deep .reschedule-options-editor {
    .v-card {
      box-shadow: none !important;
      border: 1px solid #e0e0e0;
    }
  }
}

.preview-section {
  min-width: 0;

  // md 以上（≥960px）顯示左邊框
  @media (min-width: 960px) {
    border-left: 1px solid #e0e0e0;
    padding-left: 24px;
  }

  // sm 以下（<960px）顯示上邊框
  @media (max-width: 959px) {
    border-top: 1px solid #e0e0e0;
    padding-top: 24px;
    margin-top: 24px;
  }
}

.edit-controls {
  flex-shrink: 0;
}

.reschedule-settings-page.editing-mode {
  padding-bottom: 120px; // 為底部固定按鈕留空間
}

.fixed-bottom-bar {
  position: fixed;
  display: flex;
  z-index: 100;

  // 手機版：全寬底部
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  padding: 16px 24px;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  justify-content: flex-end;
  gap: 12px;

  // 桌面版：置中，按鈕靠右
  @media (min-width: 960px) {
    bottom: 24px;
    left: 50%;
    right: auto;
    transform: translateX(-50%);
    width: 80%;
    max-width: 1200px;
    background: #F7F9FB;
    padding: 16px 32px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
    border-radius: 12px;
    justify-content: flex-end;
  }
}
</style>
